import * as Yup from 'yup';
import { useSnackbar } from 'notistack';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMemo, useState, useEffect, SyntheticEvent } from 'react';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import { Skeleton } from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import Typography from '@mui/material/Typography';
import CardHeader from '@mui/material/CardHeader';
import LoadingButton from '@mui/lab/LoadingButton';
import CardContent from '@mui/material/CardContent';
import CardActions from '@mui/material/CardActions';
import Chip, { ChipProps } from '@mui/material/Chip';

import { useTranslate } from 'src/locales';
import { Category } from 'src/services/utils/utils.types';
import { SkillType, VisibilityRule } from 'src/services/jobs/jobs.types';
import { useSetCandidateProfileMutation } from 'src/services/candidates/candidates.service';
import { useGetSkillsQuery, useSearchCategoriesQuery } from 'src/services/utils/utils.service';
import { generateCandidateProfileErrorMessage } from 'src/services/candidates/candidates.utils';
import {
  CandidateProfile,
  CandidateProfileSectionType,
} from 'src/services/candidates/candidates.types';

import FormProvider from 'src/components/hook-form';
import RHFAutocomplete from 'src/components/hook-form/rhf-autocomplete';
import HighlightedText from 'src/components/highlight-text/highlight-text';
import RHFVisibilityToggle from 'src/components/hook-form/rhf-visibility-toggle';

type LabelledSkillType = Exclude<SkillType, SkillType.SKILL>;

/** Chip colour per skill type. Plain and unclassified skills keep the default chip (JT1-1692). */
const SKILL_TYPE_COLOR: Record<LabelledSkillType, Exclude<ChipProps['color'], undefined>> = {
  [SkillType.SYSTEM]: 'info',
  [SkillType.METHODOLOGY]: 'secondary',
  [SkillType.CERTIFICATION]: 'success',
};

const LABELLED_TYPES = Object.keys(SKILL_TYPE_COLOR) as LabelledSkillType[];

const isLabelled = (type?: SkillType | null): type is LabelledSkillType =>
  !!type && type !== SkillType.SKILL;

type Props = {
  profile?: CandidateProfile;
  isLoading: boolean;
  mode?: 'edit' | 'view';
  onUpdate?: () => void;
  /** Search terms to highlight inside the skill chips (JT1-1693). */
  highlightTerms?: string[];
};

export default function ProfileSkillsEditor({ profile, isLoading, mode = 'edit', onUpdate, highlightTerms }: Props) {
  const [updateProfile] = useSetCandidateProfileMutation();

  const { t } = useTranslate();

  const { enqueueSnackbar } = useSnackbar();

  const [skillName, setSkillName] = useState('');

  const { currentData: skillsData } = useGetSkillsQuery({
    page: 1,
    per_page: 5,
    name: skillName,
  });

  // There are 27 platform categories, so one page holds them all and the list filters locally.
  const { currentData: categoriesData } = useSearchCategoriesQuery({
    page: 1,
    per_page: 50,
    name: '',
  });

  // The form edits skill names, so each chip looks its type up by name.
  const typeByName = useMemo(
    () => new Map((profile?.skills ?? []).map((skill) => [skill.name.toLowerCase(), skill.type])),
    [profile?.skills]
  );

  const defaultValues = {
    skills: [] as string[],
    industries: [] as Category[],
    skills_visibility: true,
  };

  const validationSchema = Yup.object().shape({
    skills: Yup.array().of(Yup.string().defined()),
    industries: Yup.array().of(
      Yup.object().shape({
        id: Yup.string().defined(),
        name: Yup.string().defined(),
      })
    ),
    skills_visibility: Yup.boolean(),
  });

  const formMethods = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues,
    mode: 'all',
  });

  const {
    handleSubmit,
    formState: { isSubmitting, isValid, isDirty, dirtyFields },
    watch,
    reset,
  } = formMethods;

  const currentSkills = watch('skills');

  const setProfileData = () => {
    if (!profile || isLoading) return;

    reset(
      {
        skills: profile?.skills?.map((skill) => skill.name) ?? [],
        industries: profile?.industries ?? [],
        skills_visibility:
          profile.sections?.find(
            (section) => section.section_type === CandidateProfileSectionType.SKILLS
          )?.visibility_rule === VisibilityRule.PUBLIC,
      },
      { keepDirty: false }
    );
  };

  useEffect(() => {
    setProfileData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, profile]);

  const onSubmit = handleSubmit(async (data) => {
    try {
      await updateProfile({
        id: profile?.id as string,
        data: {
          skills: data.skills ? data.skills?.map((skill) => ({ name: skill })) : undefined,
          // Sent only when edited, so saving skills never clears industries this view did not load.
          industries: dirtyFields.industries
            ? data.industries?.map((industry) => industry.id)
            : undefined,
          sections: [
            {
              type: CandidateProfileSectionType.SKILLS,
              visibility_rule: data.skills_visibility
                ? VisibilityRule.PUBLIC
                : VisibilityRule.HIDDEN,
            },
          ],
        },
      }).unwrap();

      onUpdate?.();

    } catch (e) {
      console.log(e);

      enqueueSnackbar(generateCandidateProfileErrorMessage(e), { variant: 'error' });
    }

    reset({}, { keepDirty: false });
  });

  const skills_visibility = watch('skills_visibility');

  const presentTypes = LABELLED_TYPES.filter((type) =>
    currentSkills?.some((name) => typeByName.get(name.toLowerCase()) === type)
  );

  const typeLabel = (type: LabelledSkillType) => t(`profile.sections.skills.types.${type}`);

  const onSearchSkill = (_event: SyntheticEvent, value: string) => setSkillName(value);

  const renderForm = () => (
    <>
      {
        isLoading ? (
          <Grid
            xs={12}
          >
            <Skeleton variant="rectangular" height={100} />
          </Grid>
        ) : (
          <FormProvider methods={formMethods} onSubmit={onSubmit}>
            <Grid container spacing={3}>
              <Grid xs={12}>
                <RHFAutocomplete
                  fullWidth
                  freeSolo
                  multiple
                  onInputChange={onSearchSkill}
                  name="skills"
                  label={t('jobs.create.form.skills.label')}
                  placeholder={!currentSkills?.length ? t('jobs.create.form.skills.placeholder') : undefined}
                  options={skillsData?.results?.map((option) => option.name) ?? []}
                  disabled={isLoading || mode === 'view'}
                  // Coloured by skill type (JT1-1692), with search terms highlighted (JT1-1693).
                  renderTags={(values: string[], getTagProps) => values.map((value, index) => {
                    const type = typeByName.get(value.toLowerCase());

                    return (
                      <Chip
                        {...getTagProps({ index })}
                        key={value}
                        size={highlightTerms?.length ? 'small' : undefined}
                        variant={isLabelled(type) ? 'soft' : undefined}
                        color={isLabelled(type) ? SKILL_TYPE_COLOR[type] : 'default'}
                        title={isLabelled(type) ? typeLabel(type) : undefined}
                        label={highlightTerms?.length ? <HighlightedText text={value} terms={highlightTerms} /> : value}
                      />
                    );
                  })}
                />
                {presentTypes.length > 0 && (
                  <Stack direction="row" spacing={2} flexWrap="wrap" sx={{ mt: 1 }}>
                    {presentTypes.map((type) => (
                      <Stack key={type} direction="row" alignItems="center" spacing={0.75}>
                        <Box
                          sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: `${SKILL_TYPE_COLOR[type]}.main` }}
                        />
                        <Typography variant="caption" color="text.secondary">
                          {typeLabel(type)}
                        </Typography>
                      </Stack>
                    ))}
                  </Stack>
                )}
              </Grid>
              <Grid xs={12}>
                <RHFAutocomplete
                  fullWidth
                  multiple
                  name="industries"
                  label={t('profile.sections.skills.industries')}
                  options={categoriesData?.results ?? []}
                  getOptionLabel={(option) => (option as Category).name}
                  isOptionEqualToValue={(option, value) => (option as Category).id === (value as Category).id}
                  disabled={isLoading || mode === 'view'}
                />
              </Grid>
            </Grid>
            {
              mode === 'edit' && (
                <CardActions sx={{ px: 0, pt: 2, justifyContent: 'flex-end' }}>
                  <RHFVisibilityToggle
                    name="skills_visibility"
                    labelPosition="left"
                    checked={skills_visibility}
                    onLabel={t('common.visible')}
                    offLabel={t('common.hidden')}
                    tooltip={t('profile.form.toggle_tooltip')}
                  />

                  <LoadingButton
                    variant="contained"
                    color="success"
                    type="submit"
                    loading={isSubmitting}
                    disabled={!isValid || !isDirty}
                  >
                    {t('common.save')}
                  </LoadingButton>
                </CardActions>
              )
            }
          </FormProvider>
        )
      }
    </>
  );

  return (
    <Card>
      <CardHeader
        title={isLoading ? <Skeleton width={200} /> : t('profile.sections.skills.title')}
      />
      <CardContent>{renderForm()}</CardContent>
    </Card>
  );
}
